import { Preferences } from "@capacitor/preferences";
import { User } from "../contexts/AuthContext";
import { AllCountryCodes } from "./AllCountryCodes";
import { apiClient } from "../services/api";

export const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

export const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring" as const, stiffness: 100, damping: 15 },
  },
};
const getMaxCharLength = () => {
  const width = window.innerWidth;
  if (width < 640) return 15;
  // if (width < 1024) return 40;
  if (width < 1440) return 100;
  return 100;
};

export const capitalize = (str: string) =>
  str
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");

// Shown when a profile carries no name, email or phone at all.
export const UNKNOWN_USER_LABEL = "Arc Connect User";

/**
 * The parts of a person any display helper needs. Every field is optional AND nullable: both
 * backends return null for an unset name, and a phone-only account has no email at all.
 */
export interface ProfileNameParts {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

/**
 * A person's best display label. ⛔ TOTAL — always returns a string, for ANY input including
 * `null`/`undefined`. This is the ONE shared display-name helper; do not copy a
 * `first && last ? ... : first || last || email || phone` chain into a component again.
 *
 * ⛔ Why totality is load-bearing: App.tsx's ErrorBoundary is the app's ONLY one and it renders
 * `null` (TD-165), so a TypeError thrown while rendering one row unmounts the ENTIRE app — a
 * permanent blank screen on web and native. Callers do `.trim()`/`.slice()` on the result, so a
 * nullish return here is an app-blanking bug, not a cosmetic one. Keep the `request || {}` and
 * the final string fallback.
 *
 * @param maxLength optional cap, e.g. the tighter header greeting.
 */
export const getUserName = (
  request?: ProfileNameParts | string | null,
  maxLength?: number
): string => {

  const truncate = (str: string) => {
    const max = maxLength ?? getMaxCharLength();
    return str.length > max ? str.slice(0, max) + ".." : str;
  };
  if (typeof request === "string") {
    return truncate(capitalize(request));
  }

  const { first_name, last_name, email, phone } = request || {};

  const name =
    (first_name && last_name && `${first_name} ${last_name}`) ||
    first_name ||
    last_name ||
    email?.split("@")[0] ||
    phone ||
    UNKNOWN_USER_LABEL;
  return truncate(
    name === first_name || name === last_name || name.includes(" ")
      ? capitalize(name)
      : name
  );
};
/** First char, uppercased. "" if it starts with a digit or "+", so phone numbers never become initials. */
const initialOf = (value?: string | null): string => {
  const char = value?.trim().charAt(0) || "";
  if (!char || /[\d+]/.test(char)) return "";
  return char.toUpperCase();
};

/**
 * Avatar initials: any name part -> email -> "U".
 * Phone-only profiles get "U", since a digit is not an initial.
 *
 * ⛔ TOTAL — always returns a string ("U" when nothing is known), for ANY input including
 * `null`/`undefined`. App.tsx's ErrorBoundary is the app's ONLY one and renders `null`
 * (TD-165), so a TypeError thrown while rendering one avatar blanks the WHOLE app.
 */
export const getInitials = (
  first?: string | null,
  last?: string | null,
  email?: string | null
): string => {
  const firstInitial = initialOf(first);
  const lastInitial = initialOf(last) || initialOf(first?.split(" ")[1]);

  if (firstInitial || lastInitial) {
    return firstInitial + lastInitial;
  }

  return initialOf(email) || "U";
};
// types.ts
export interface Following {
  id: number;
  name: string;
  avatar: string;
  initials: string | null;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  profileId: number;
}


// Profile display for the Reflections feed and the following-list popup.
// (Elsewhere the app still uses getUserName, which follows an older rule.)
//   Label     name -> email -> phone -> UNKNOWN_USER_LABEL
//   Initials  name -> email letter -> "U"
// Label is returned in full; call sites truncate with CSS + a title tooltip.

export interface ProfileDisplayInput {
  /** Composed name, e.g. Post.userName. Email/phone-looking values are re-filed, not used as a name. */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
}

export interface ProfileDisplay {
  /** Label to render, untruncated. */
  name: string;
  /** Fallback letters when there is no avatar image. */
  initials: string;
  /** Image URL, or "". */
  avatar: string;
}

const looksLikeEmail = (value: string): boolean => value.includes("@");

const looksLikePhone = (value: string): boolean =>
  /^[+(]?\d[\d\s()-]*$/.test(value);

export const resolveProfileDisplay = (
  input: ProfileDisplayInput
): ProfileDisplay => {
  const candidate =
    input.name?.trim() ||
    [input.firstName, input.lastName]
      .map((part) => part?.trim() || "")
      .filter(Boolean)
      .join(" ");

  // A contact detail in the name slot is re-filed, not dropped.
  const nameIsEmail = !!candidate && looksLikeEmail(candidate);
  const nameIsPhone = !!candidate && looksLikePhone(candidate);

  const name = nameIsEmail || nameIsPhone ? "" : candidate;
  const email = input.email?.trim() || (nameIsEmail ? candidate : "");
  const phone = input.phone?.trim() || (nameIsPhone ? candidate : "");

  // The domain is dropped: it is noise in a label and needless PII on screen.
  // getUserName labels on the local part too, so both stay in step.
  const emailLocal = email.split("@")[0];
  const emailLabel = emailLocal
    ? emailLocal.charAt(0).toUpperCase() + emailLocal.slice(1)
    : "";

  // name -> email local part (capitalised, so it reads like a name) -> phone
  const label = name || emailLabel || phone || UNKNOWN_USER_LABEL;

  return {
    name: label,
    // getInitials already applies name -> email -> "U".
    initials: getInitials(name, "", email),
    avatar: input.avatar?.trim() || "",
  };
};

// followerUtils.ts
/**
 * Maps raw follower data array to Following interface array
 * @param following - Array of follower data objects
 * @returns Array of mapped Following objects
 */
export const mapFollowers = (following: any[] = []): Following[] => {
  return following.map((data: any, index: number) => ({
    id: data.profile?.id || index + 1,
    name:
      data.profile?.first_name && data.profile?.last_name
        ? `${data.profile.first_name} ${data.profile.last_name}`
        : data.profile?.first_name ||
        data.profile?.last_name ||
        data.profile?.email ||
        data.profile?.phone ||
        `User ${index + 1}`,
    avatar: data.profile?.avatar || "",
    initials: getInitials(
      data.profile?.first_name || "",
      data.profile?.last_name || "",
      data.profile?.email
    ),
    email: data.profile?.email,
    phone: data.profile?.phone,
    firstName: data.profile?.first_name,
    lastName: data.profile?.last_name,
    username: data.profile?.username,
    profileId: data.profile?.id || index + 1,
  }));
};

export interface Invitation {
  inviterProfileId: string;
  userIdentifier: string;
  createdAt: Date;
  randomComponent: string;
  isValid: boolean;
  inviterName: string;
  invitedTo?: string;
}
export const formatPhoneNumber = (
  value: string,
  CountryCode: string,
  returnCountryCode = false
) => {
  const cleaned = value.replace(/[^\d+]/g, "");
  const formattedNumber = formatByPattern(CountryCode, cleaned);
  if (returnCountryCode) {
    return `${CountryCode === null ? "" : CountryCode} ${formattedNumber
      ?.replaceAll(CountryCode, "")
      .trim()}`;
  }
  return formattedNumber;
};
const formatByPattern = (CountryCode: string, digits: string) => {
  if (digits.length === 0) {
    return "";
  }
  const country = AllCountryCodes[CountryCode as keyof typeof AllCountryCodes];
  if (!country) return digits;

  // Special handling for US/Canada - return only formatted number without country code
  if (CountryCode === "+1") {
    if (digits.length <= 3) {
      return `(${digits}`;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
  }

  // Parse the pattern to understand how to format
  const pattern = country.pattern;
  const digitPattern = pattern.replace(CountryCode, "").trim();

  // Parse pattern to extract formatting groups
  const formatGroups: {
    type: "digits" | "separator";
    count?: number;
    char?: string;
  }[] = [];
  let currentGroup = "";
  for (let i = 0; i < digitPattern.length; i++) {
    const char = digitPattern[i];
    if (char === "#") {
      currentGroup += "#";
    } else if (char === " " || char === "-" || char === "(" || char === ")") {
      if (currentGroup) {
        formatGroups.push({ type: "digits", count: currentGroup.length });
        currentGroup = "";
      }
      if (char !== " ") {
        formatGroups.push({ type: "separator", char: char });
      } else {
        formatGroups.push({ type: "separator", char: " " });
      }
    }
  }
  if (currentGroup) {
    formatGroups.push({ type: "digits", count: currentGroup.length });
  }

  // Apply formatting based on parsed groups - return only formatted number
  let result = "";
  let digitPos = 0;
  let isFirstGroup = true;

  for (const group of formatGroups) {
    if (group.type === "digits" && typeof group.count === "number") {
      const groupDigits = digits.slice(digitPos, digitPos + group.count);
      if (groupDigits.length > 0) {
        // Don't add space before first group, but add space before subsequent digit groups
        if (
          !isFirstGroup &&
          result &&
          !result.endsWith(" ") &&
          !result.endsWith("(") &&
          !result.endsWith("-")
        ) {
          result += " ";
        }
        result += groupDigits;
        digitPos += group.count;
        isFirstGroup = false;
      }
      if (digitPos >= digits.length) break;
    } else if (group.type === "separator" && digitPos < digits.length) {
      const remainingDigits = digits.slice(digitPos);
      if (remainingDigits.length > 0 && group.char) {
        result += group.char;
      }
    }
  }

  return result;
};

export const wrapText = (text: string, maxChars: number): string[] => {
  // Guard
  if (!text) return [""];
  const words = String(text).split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    // If adding the next word stays within limit, append it
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }

    // Otherwise flush current line (if any)
    if (current) {
      lines.push(current);
      current = "";
    }

    // If the single word is longer than maxChars, break it into chunks
    if (word.length > maxChars) {
      let start = 0;
      while (start < word.length) {
        lines.push(word.substring(start, start + maxChars));
        start += maxChars;
      }
    } else {
      // Start new line with the word
      current = word;
    }
  }

  if (current) lines.push(current);
  return lines;
};

// Matches the 20-char limit used elsewhere (AllFollowingList, MyBioMetric).
const POST_NAME_MAX_LENGTH = 20;

/**
 * A post/comment author's display label. ⛔ TOTAL — always a string, for ANY input.
 *
 * Two bugs lived here (D143.5):
 *  - shape (A): it dereferenced `data.first_name` on an unchecked parameter. Its caller
 *    `ReplyModal.renderComment` optional-chains `comment.profile?.first_name` on the two lines
 *    ABOVE the call and then passes that same possibly-undefined `comment.profile` in bare. That
 *    is a render path with no try/catch, so the TypeError reached App.tsx's ErrorBoundary —
 *    which renders `null` (TD-165) — and blanked the WHOLE app from one comment.
 *  - shape (B): with no final `else` the function's return type was `string | undefined`.
 *    Unreachable while `type` stays a two-member literal union, but it is the same latent trap
 *    that made `getDisplayName` return `undefined` into a caller's `.trim()`.
 *
 * @param fallback used when the record has no name, e.g. the author's email.
 */
export const postUserName = (
  data: (ProfileNameParts & { userName?: string | null }) | null | undefined,
  type: "post" | "comment",
  fallback?: string
): string => {
  if (type === "comment") {
    if (data?.first_name && data?.last_name) {
      if (data.first_name.length > 7 || data.last_name.length > 7) {
        return (
          data.first_name.slice(0, 7) +
          ".." +
          " " +
          data.last_name.slice(0, 7) +
          ".."
        );
      } else {
        return data.first_name + " " + data.last_name;
      }
    }
    return (
      data?.userName ||
      data?.first_name ||
      data?.last_name ||
      fallback?.trim() ||
      data?.email ||
      data?.phone ||
      UNKNOWN_USER_LABEL
    );
  }
  // ⛔ Unconditional, not `else if (type === "post")`. That branch left an implicit
  // `return undefined` for any other value of `type`.
  const label = data?.userName?.trim() || fallback?.trim() || "";

  if (!label) {
    return UNKNOWN_USER_LABEL;
  }

  if (label.length > POST_NAME_MAX_LENGTH) {
    return label.slice(0, POST_NAME_MAX_LENGTH) + "..";
  }

  return label;
};

export const detectDeviceType = (
  isHealthKitEnabled?: boolean
): "apple" | "google" => {
  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/android/i.test(userAgent)) return "google";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "apple";
  return "google";
};
export const getIsoDate = (d: Date) => {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) {
    throw new Error("Invalid date provided");
  }
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
  return today;
};

export const RemoveList = [
  "notifications",
  "action-required",
  "dataSharingStats",
];

// Helper function to get responsive layout configuration
export const getResponsiveLayout = (width: number) => {
  // Large desktop (>1200px)
  if (width > 1200) {
    return {
      layout: "desktop",
      boxWidth: "22%",
      gapBetweenBoxes: 40,
      boxHeight: "120px",
      dropdownWidth: 170,
      fontSize: "11px",
      valueSpacing: "4px",
    };
  }
  // Standard desktop/iPad landscape (900-1200px)
  else if (width > 900) {
    return {
      layout: "desktop",
      boxWidth: "24%",
      gapBetweenBoxes: 20,
      boxHeight: "130px",
      dropdownWidth: 165,
      fontSize: "11px",
      valueSpacing: "4px",
    };
  }
  // iPad portrait / small desktop (700-900px)
  else if (width > 700) {
    return {
      layout: "portrait",
      boxWidth: "28%",
      gapBetweenBoxes: 24,
      gapBeforeDropdown: 10,
      boxHeight: "130px",
      dropdownWidth: 160,
      fontSize: "11px",
      valueSpacing: "4px",
    };
  }
  // Mobile landscape (600-700px)
  else if (width > 600) {
    return {
      layout: "landscape",
      boxWidth: "32%",
      gapBetweenBoxes: 10,
      gapBeforeDropdown: 8,
      boxHeight: "125px",
      dropdownWidth: 145,
      fontSize: "11px",
      valueSpacing: "4px",
    };
  }
  // Mobile portrait (<600px)
  else {
    return {
      layout: "mobile",
      dropdownWidth: 120,
      fontSize: "11px",
      valueSpacing: "0px",
    };
  }
};

export const isValidHttpImage = (src: unknown): boolean => {
  return typeof src === "string" && /^https?:\/\//i.test(src);
};


export const formatDate = (date: Date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error("Invalid date provided");
  }
  return getIsoDate(date);
}


export const getFileNamePrefix = (user: any) => {
  switch (true) {
    case !user:
      return "ArcConnectReport";
    case !!(user.first_name && user.last_name):
      return `${user.first_name}_${user.last_name}`;
    case !!user.first_name:
      return user.first_name;
    case !!user.last_name:
      return user.last_name;
    case !!user.email:
      return user.email.split("@")[0] || "ArcConnectReport";
    default:
      return "ArcConnectReport";
  }
};

export const buildQueryParams = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      searchParams.append(key, String(value));
    }
  }
  return searchParams.toString();
}

export const formatDuration = (decimalMinutes: number): string => {
  const totalSeconds = Math.round(decimalMinutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s} sec`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} sec`;
};

export const parseTabToDateRange = (tabValue: string | null | undefined) => {
  if (!tabValue) return "";
  let decoded = "";
  try {
    decoded = decodeURIComponent(String(tabValue)).trim();
  }
  catch (e) {
    console.error("Error decoding tab value:", e);
    return "";
  }
  const today = new Date();

  // Today
  if (decoded === "Today") {
    const d = formatDate(today);
    return `${d} to ${d}`;
  }

  // Yesterday
  if (decoded === "Yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const d = formatDate(y);
    return `${d} to ${d}`;
  }

  // Date range: Dec 29 - Jan 4, 2026
  // Date range like "Dec 22 - 28, 2025" or "Dec 29 - Jan 4, 2026"
  if (decoded.toLowerCase() === "this week") {
    const startOfWeek = new Date(today);
    // start Monday
    startOfWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return `${formatDate(startOfWeek)} to ${formatDate(endOfWeek)}`;
  }

  if (decoded.includes(" - ")) {
    // extract year if present at end like ", 2025"
    const yearMatch = decoded.match(/,\s*(\d{4})$/);
    const year = yearMatch ? Number(yearMatch[1]) : today.getFullYear();

    // remove trailing ", 2025" if present
    const rangeStr = decoded.replace(/,\s*\d{4}$/, "").trim();
    const [startStrRaw, endStrRaw] = rangeStr.split(" - ").map((s) => s.trim());

    // If end part doesn't include a month, reuse start's month
    const startStr = startStrRaw;
    const endStr =
      /^[A-Za-z]/.test(endStrRaw) || /\d{1,2}\s+[A-Za-z]/i.test(endStrRaw)
        ? endStrRaw
        : `${startStr.split(/\s+/)[0]} ${endStrRaw}`;

    const startDate = new Date(`${startStr} ${year}`);
    const endDate = new Date(`${endStr} ${year}`);

    // If parsing produced invalid dates, fallback to today's year-aware parsing
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      const s = new Date(`${startStrRaw} ${year}`);
      const e = new Date(`${endStrRaw} ${year}`);
      if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
        return `${formatDate(s)} to ${formatDate(e)}`;
      }
      return "";
    }

    return `${formatDate(startDate)} to ${formatDate(endDate)}`;
  }

  // Year only: "2026"
  if (/^\d{4}$/.test(decoded)) {
    const year = Number(decoded);
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return `${formatDate(start)} to ${formatDate(end)}`;
  }

  // Month year: January 2026
  if (/^[A-Za-z]+\s\d{4}$/.test(decoded)) {
    const MONTH_NAMES = ["january","february","march","april","may","june","july","august","september","october","november","december"];
    const [monthStr, yearStr] = decoded.split(" ");
    const monthIndex = MONTH_NAMES.indexOf(monthStr.toLowerCase());
    const year = Number(yearStr);
    if (monthIndex !== -1 && !isNaN(year)) {
      const start = new Date(year, monthIndex, 1);
      const end = new Date(year, monthIndex + 1, 0);
      return `${formatDate(start)} to ${formatDate(end)}`;
    }
  }

  // Single date: Mon, Jan 26, 2026
  if (decoded.includes(",")) {
    const d = new Date(decoded);
    const f = formatDate(d);
    return `${f} to ${f}`;
  }

  return "";
};
export const convertShortDateFormat = (dateTimeStr: string) => {
  if (!dateTimeStr) return "";

  const [datePart, timePart] = dateTimeStr.split(",");
  const dateParts = datePart.trim().split("-");

  if (dateParts.length === 3) {
    const [yyyy, mm, dd] = dateParts.map(Number);

    // ✅ Safe constructor (month is 0-based)
    const dateObj = new Date(yyyy, mm - 1, dd);

    const month = dateObj
      .toLocaleString("en-US", { month: "short" })
      .replace(/^./, c => c.toUpperCase());

    const day = String(dateObj.getDate()).padStart(2, "0");
    const year = dateObj.getFullYear();

    return `${month} ${day}, ${year}${timePart ? " " + timePart.trim() : ""}`;
  }

  return dateTimeStr;
};

export type DateFormat = "MMM DD YYYY" | "DD MMM YYYY" | "YYYY MMM DD";
export const MONTHS_ABBR = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

export function formatDateByConfig(
  dayIndex: number,
  monthIndex: number,
  yearIndex: number,
  daysArray: string[],
  monthsArray: string[],
  yearsArray: string[],
  format: DateFormat,
): string {
  if (daysArray.length === 0) return "";
  const safeDay = Math.min(dayIndex, daysArray.length - 1);
  const day = daysArray[safeDay] ?? "";
  const month = monthsArray[monthIndex] ?? "";
  const year = yearsArray[yearIndex] ?? "";
  switch (format) {
    case "DD MMM YYYY": return `${day} ${month} ${year}`;
    case "MMM DD YYYY": return `${month} ${day} ${year}`;
    case "YYYY MMM DD": return `${year} ${month} ${day}`;
    default: return `${month} ${day} ${year}`;
  }
}
// Formats an ISO date string (YYYY-MM-DD) using DATE_FORMAT.
export function formatISOByConfig(iso: string, format: DateFormat): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const month = MONTHS_ABBR[(parseInt(m, 10) - 1)] ?? "";
  const day = d ?? "";
  const year = y ?? "";
  switch (format) {
    case "DD MMM YYYY": return `${day} ${month} ${year}`;
    case "MMM DD YYYY": return `${month} ${day} ${year}`;
    case "YYYY MMM DD": return `${year} ${month} ${day}`;
    default: return `${month} ${day} ${year}`;
  }
}
// Each element is "MMM" | "DD" | "YYYY".
export function getWheelOrder(format: DateFormat): Array<"MMM" | "DD" | "YYYY"> {
  switch (format) {
    case "DD MMM YYYY": return ["DD", "MMM", "YYYY"];
    case "YYYY MMM DD": return ["YYYY", "MMM", "DD"];
    case "MMM DD YYYY":
    default: return ["MMM", "DD", "YYYY"];
  }
}

// Formats an ISO date string (YYYY-MM-DD) to MMMDDYYYY format for filenames
export function formatDateForFilename(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const month = MONTHS_ABBR[(parseInt(m, 10) - 1)] ?? "";
  const day = d ?? "";
  const year = y ?? "";
  return `${month}${day}${year}`;
}
